import { useCallback, useMemo, useRef, useState } from "react";
import {
  addDays,
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { getWeekDays, isSameDay } from "@/lib/agenda-time";
import {
  appointmentDuration,
  computeEndTime,
  parseSlotId,
  SEM_SALA,
} from "@/lib/agenda-drag";
import { colunaDaConsulta, colunasDaAgenda, salaDoSlot, type SalaDaColuna } from "@/lib/agenda-colunas";
import { CabecalhoDaSala } from "./CabecalhoDaSala";
import { layoutAppointments, type LaidOut } from "@/lib/agenda-overlap";
import { resolveCardColor } from "@/lib/agenda-colors";
import { isOrderOverdue } from "@/lib/prosthesis-urgency";
import { TimeColumn } from "./TimeColumn";
import { DayColumn } from "./DayColumn";
import { DragFloatingCard } from "./DragFloatingCard";
import { useFeature } from "@/lib/use-feature";
import { usePermissao } from "@/lib/use-permissao";

type HoveredRange = {
  date: Date;
  start: string;
  end: string;
  skipSlotHighlight?: boolean;
  /** Posição horizontal do destaque (0-1). Quando há appointments em outras
   *  colunas no mesmo range, cobre só o gap livre onde o mouse está. */
  leftFrac?: number;
  widthFrac?: number;
  /** Agenda por sala: a coluna (sala) onde está o destaque. */
  sala?: string;
};

type Props = {
  anchorDate: Date;
  settings: AgendaSettings;
  viewMode?: "day" | "week" | "fortnight" | "month";
  /** Ids de profissionais selecionados no filtro. Se vazio: mostra nada. Se undefined: mostra tudo. */
  selectedDentistIds?: string[];
  /** Ids de serviços selecionados no filtro. Vazio ou undefined: sem filtro (mostra tudo). */
  selectedServiceIds?: string[];
  /** Compromissos de OUTRAS organizações (Agenda Pessoal) — mesclados na mesma grade, sempre
   *  só-leitura (ver `organization` em cada item). Não passam pelo filtro de dentista/serviço. */
  extraAppointments?: any[];
  /** Se false, omite os compromissos da própria organização ativa (usado no filtro
   *  "Organizações" da Agenda Pessoal — mostra só `extraAppointments`). Default true. */
  includeOwn?: boolean;
  onAppointmentEdit?: (id: string) => void;
  onAppointmentStatusChange?: (id: string, status: AppointmentStatus) => void;
  onAppointmentIniciarAtendimento?: (id: string) => void;
  onAppointmentEncerrarAtendimento?: (id: string) => void;
  atendimentoPendente?: boolean;
  onAppointmentDelete?: (id: string) => void;
  onSlotDoubleClick?: (date: Date, startTime: string, salaId?: string | null) => void;
  /** `salaId` só vem ao soltar na coluna de outra sala (agenda por sala). */
  onAppointmentMove?: (id: string, newDate: Date, newStart: string, newEnd: string, salaId?: string | null) => void;
  /** Agenda por sala (só na vista de dia): uma coluna por sala, mais "Sem sala". */
  salas?: SalaDaColuna[] | null;
  /** Clique na faixa de prazo de um pedido de prótese (dia inteiro, sem hora). */
  onOrderClick?: (id: string) => void;
};

export function WeekView({
  anchorDate,
  settings,
  viewMode = "week",
  selectedDentistIds,
  selectedServiceIds,
  extraAppointments,
  includeOwn = true,
  onAppointmentEdit,
  onAppointmentStatusChange,
  onAppointmentIniciarAtendimento,
  onAppointmentEncerrarAtendimento,
  atendimentoPendente,
  onAppointmentDelete,
  onSlotDoubleClick,
  onAppointmentMove,
  onOrderClick,
  salas,
}: Props) {
  const porSala = viewMode === "day" && !!salas;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoveredRange, setHoveredRange] = useState<HoveredRange | null>(null);

  const today = new Date();

  const visibleDays = useMemo(() => {
    if (viewMode === "day") return [anchorDate];

    const weekDays = getWeekDays(anchorDate);

    if (viewMode === "fortnight") {
      const start = startOfWeek(anchorDate, { weekStartsOn: 0 });
      return Array.from({ length: 14 }).map((_, i) => addDays(start, i));
    }

    if (viewMode === "month") {
      const start = startOfMonth(anchorDate);
      const end = endOfMonth(anchorDate);
      const days: Date[] = [];
      let cur = start;
      while (cur <= end) {
        days.push(new Date(cur));
        cur = addDays(cur, 1);
      }
      return days;
    }

    return settings.showWeekends
      ? weekDays
      : weekDays.filter((d) => d.getDay() !== 0 && d.getDay() !== 6);
  }, [viewMode, anchorDate, settings.showWeekends]);

  const slotDuration = Number(settings.slotDuration);

  const rangeStart = useMemo(
    () => startOfDay(visibleDays[0] ?? anchorDate),
    [visibleDays, anchorDate],
  );
  const rangeEnd = useMemo(
    () => endOfDay(visibleDays[visibleDays.length - 1] ?? anchorDate),
    [visibleDays, anchorDate],
  );

  const deadlineOrdersQuery = trpc.prosthesis.listByDeadlineRange.useQuery({ rangeStart, rangeEnd });
  const deadlineOrdersByDay = useMemo(() => {
    const map = new Map<string, Array<{ id: string; patientName: string; overdue: boolean }>>();
    for (const o of (deadlineOrdersQuery.data ?? []) as any[]) {
      if (!o.deadline) continue;
      const key = startOfDay(new Date(o.deadline)).toISOString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({ id: o.id, patientName: o.patientName, overdue: isOrderOverdue(o.deadline, o.status) });
    }
    return map;
  }, [deadlineOrdersQuery.data]);

  const apptsQuery = trpc.appointment.listByRange.useQuery(
    { rangeStart, rangeEnd },
    { enabled: includeOwn },
  );

  // `enabled: false` só impede o fetch — não esvazia o cache. A página da Agenda mantém uma query
  // de mesma chave sempre ligada (pra achar consulta por id), então o dado próprio continua
  // chegando aqui mesmo com includeOwn=false. Quem decide o que entra na grade é este filtro.
  const proprias = useMemo(
    () => (includeOwn ? ((apptsQuery.data ?? []) as any[]) : []),
    [includeOwn, apptsQuery.data],
  );

  // Mapa id → nome LIVE do dentista. Sobrescreve appointment.dentistName
  // (denormalizado) pra mostrar sempre o nome atual em renderização,
  // mesmo quando o user editou o perfil após criação da consulta.
  const dentistsQuery = trpc.organization.listDentists.useQuery(undefined, {
    staleTime: 60_000,
  });
  const dentistNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of (dentistsQuery.data ?? []) as Array<{
      id: string;
      name: string;
    }>) {
      map.set(d.id, d.name);
    }
    return map;
  }, [dentistsQuery.data]);

  // Pedidos de prótese em aberto pros pacientes visíveis — pra badge/detalhe no card.
  const visiblePatientIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of proprias) {
      if (a.patientId) ids.add(a.patientId);
    }
    return Array.from(ids);
  }, [proprias]);

  const prosthesisOrdersQuery = trpc.appointment.openProsthesisOrdersByPatientIds.useQuery(
    { patientIds: visiblePatientIds },
    { enabled: visiblePatientIds.length > 0 },
  );

  // Alertas da anamnese (só os códigos) — só pra quem pode ver dado de saúde.
  const temAnamnese = useFeature("patient.anamnese");
  const podeVerAnamnese = usePermissao("anamnese.view");
  const alertasQuery = trpc.anamnese.alertasPorPacientes.useQuery(
    { patientIds: visiblePatientIds },
    { enabled: temAnamnese && podeVerAnamnese && visiblePatientIds.length > 0 },
  );

  const prosthesisOrdersByPatient = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const o of (prosthesisOrdersQuery.data ?? []) as any[]) {
      if (!o.patientId) continue;
      if (!map.has(o.patientId)) map.set(o.patientId, []);
      map.get(o.patientId)!.push(o);
    }
    return map;
  }, [prosthesisOrdersQuery.data]);

  const appointmentsByDay = useMemo(() => {
    const map = new Map<string, any[]>();
    const filterIds = selectedDentistIds;
    const noFilter = filterIds === undefined;
    const noServiceFilter = !selectedServiceIds || selectedServiceIds.length === 0;
    for (const a of proprias) {
      // Filtra por profissional (se selectedDentistIds foi passado).
      // null dentistId = appointments sem profissional vinculado → sempre aparecem.
      // Por sala o filtro de profissional não vale: esconder alguém faria a cadeira parecer livre.
      if (!porSala && !noFilter && a.dentistId && !filterIds.includes(a.dentistId)) continue;
      if (!porSala && !noFilter && !a.dentistId && filterIds.length === 0) continue;
      // Filtra por serviço — ao contrário do dentista, vazio/undefined = sem filtro (mostra tudo).
      // Uma consulta pode ter vários serviços: basta um deles casar.
      if (
        !noServiceFilter &&
        !(a.services ?? []).some((s: { serviceId: string }) =>
          selectedServiceIds!.includes(s.serviceId),
        )
      ) {
        continue;
      }
      const liveName = a.dentistId ? dentistNameMap.get(a.dentistId) : null;
      const openProsthesisOrders = a.patientId ? prosthesisOrdersByPatient.get(a.patientId) : undefined;
      const alertasDaAnamnese = a.patientId ? alertasQuery.data?.[a.patientId] : undefined;
      const augmented =
        liveName || openProsthesisOrders || alertasDaAnamnese?.length
          ? { ...a, ...(liveName ? { dentistName: liveName } : null), openProsthesisOrders, alertasDaAnamnese }
          : a;
      const key = colunaDaConsulta(a, porSala);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(augmented);
    }
    // Compromissos de outras organizações — sem filtro de dentista/serviço (é escopo da
    // organização ativa, não se aplica), já vêm marcados com `organization` (card read-only).
    for (const a of extraAppointments ?? []) {
      const key = colunaDaConsulta(a, porSala);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [
    proprias,
    selectedDentistIds,
    selectedServiceIds,
    dentistNameMap,
    prosthesisOrdersByPatient,
    alertasQuery.data,
    extraAppointments,
    porSala,
  ]);

  const colunas = useMemo(() => colunasDaAgenda(visibleDays, porSala ? salas! : null, proprias), [visibleDays, porSala, salas, proprias]);
  // A faixa de prazos tem a mesma altura em todas as colunas e na régua — a do dia com mais pedidos.
  const linhasDaFaixa = Math.max(0, ...colunas.map((c) => (c.sala ? 0 : (deadlineOrdersByDay.get(startOfDay(c.date).toISOString())?.length ?? 0))));

  const draggingAppt = useMemo(() => {
    if (!draggingId) return null;
    for (const list of appointmentsByDay.values()) {
      const found = list.find((a) => a.id === draggingId);
      if (found) return found;
    }
    return null;
  }, [draggingId, appointmentsByDay]);

  const sensors = useSensors(
    // distance: 2 — drag ativa quase instantâneo, mas 2px evita ativar em
    // cliques simples (popover continua funcionando no click).
    useSensor(PointerSensor, {
      activationConstraint: { distance: 2 },
    }),
  );

  const handleDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    setDraggingId(id);
    const appt = (e.active.data.current as any)?.appointment;
    if (appt) {
      setHoveredRange({
        date: new Date(appt.date),
        start: appt.startTime,
        end: appt.endTime,
        sala: porSala ? (appt.salaId ?? SEM_SALA) : undefined,
      });
    }
  };

  // RAF throttle — processa 1x por frame
  const rafRef = useRef<number | null>(null);
  const pendingOverRef = useRef<DragOverEvent | null>(null);

  const processDragOver = useCallback(() => {
    rafRef.current = null;
    const e = pendingOverRef.current;
    pendingOverRef.current = null;
    if (!e?.over) return;
    const parsed = parseSlotId(String(e.over.id));
    const appt = (e.active.data.current as any)?.appointment;
    if (!parsed || !appt) return;
    const duration = appointmentDuration(appt.startTime, appt.endTime);
    const endTime = computeEndTime(parsed.time, duration, settings.endHour);
    setHoveredRange((prev) => {
      if (
        prev &&
        prev.date.getTime() === parsed.date.getTime() &&
        prev.start === parsed.time &&
        prev.sala === salaDoSlot(parsed.salaId)
      ) {
        return prev;
      }
      return { date: parsed.date, start: parsed.time, end: endTime, sala: salaDoSlot(parsed.salaId) };
    });
  }, [settings.endHour]);

  const handleDragOver = (e: DragOverEvent) => {
    pendingOverRef.current = e;
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(processDragOver);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setDraggingId(null);
    setHoveredRange(null);
    if (!e.over || !onAppointmentMove) return;
    const parsed = parseSlotId(String(e.over.id));
    if (!parsed) return;
    const data = e.active.data.current as { appointment?: any } | undefined;
    const appt = data?.appointment;
    if (!appt) return;

    const duration = appointmentDuration(appt.startTime, appt.endTime);
    const newEnd = computeEndTime(parsed.time, duration, settings.endHour);

    const sameDate =
      new Date(appt.date).toDateString() === parsed.date.toDateString();
    const outraSala = parsed.salaId !== undefined && parsed.salaId !== (appt.salaId ?? null);
    if (sameDate && appt.startTime === parsed.time && !outraSala) return;

    onAppointmentMove(appt.id, parsed.date, parsed.time, newEnd, outraSala ? parsed.salaId : undefined);
  };

  const handleDragCancel = () => {
    setDraggingId(null);
    setHoveredRange(null);
  };

  /** Layout base — computa 1x quando appointmentsByDay muda. Nao recomputa durante drag. */
  const baseLayoutByDay = useMemo(() => {
    const map = new Map<string, LaidOut<any>[]>();
    for (const [key, list] of appointmentsByDay.entries()) {
      map.set(key, layoutAppointments(list));
    }
    return map;
  }, [appointmentsByDay]);

  // Cor do card sendo arrastado (pra DragOverlay)
  const draggingColor = draggingAppt
    ? resolveCardColor(
        {
          status: draggingAppt.status,
          color: draggingAppt.color,
          patientName: draggingAppt.patientName,
        },
        settings.cardStyle,
      )
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="h-full overflow-auto border-t border-zinc-200 bg-white dark:border-white/[0.05] dark:bg-background">
        <div
          className="flex"
          style={
            colunas.length === 1
              ? undefined
              : { minWidth: `${Math.max(720, colunas.length * (porSala ? 160 : 80) + 64)}px` }
          }
        >
          <TimeColumn
            startHour={settings.startHour}
            endHour={settings.endHour}
            slotDurationMinutes={slotDuration}
            highlightedRange={hoveredRange}
            showNowLabel={settings.timeLineMode !== "off"}
            nowLabelColor={settings.timeLineColor}
            linhasDaFaixa={linhasDaFaixa}
          />
          {colunas.map((col) => {
            const date = col.date;
            const dayIdx = date.getDay();
            const isInactive = !settings.workingDays.includes(dayIdx);
            const isWeekend = dayIdx === 0 || dayIdx === 6;
            const isTodayCol = isSameDay(date, today);

            const showNow =
              settings.timeLineMode !== "off" &&
              (settings.timeLineMode === "all" || isTodayCol);

            const dayKey = startOfDay(date).toISOString();
            const laidOut = baseLayoutByDay.get(col.chave) ?? [];

            const rangeForColumn =
              hoveredRange &&
              isSameDay(hoveredRange.date, date) &&
              (!col.sala || hoveredRange.sala === col.sala.chave) &&
              !hoveredRange.skipSlotHighlight
                ? {
                    start: hoveredRange.start,
                    end: hoveredRange.end,
                    leftFrac: hoveredRange.leftFrac,
                    widthFrac: hoveredRange.widthFrac,
                  }
                : null;

            return (
              <DayColumn
                key={col.chave}
                sala={col.sala?.chave}
                cabecalho={col.sala && <CabecalhoDaSala nome={col.sala.nome} cor={col.sala.cor} />}
                date={date}
                diaUnico={visibleDays.length === 1}
                startHour={settings.startHour}
                endHour={settings.endHour}
                slotDurationMinutes={slotDuration}
                gridLines={settings.gridLines}
                lunchBreak={settings.lunchBreak}
                showNowLine={showNow}
                nowLineVariant={isTodayCol ? "full" : "ghost"}
                nowLineStyle={settings.timeLineStyle}
                nowLineColor={settings.timeLineColor}
                nowLineHighlightToday={settings.timeLineHighlightToday}
                isWeekend={isWeekend}
                isInactive={isInactive}
                laidOutAppointments={laidOut}
                cardStyle={settings.cardStyle}
                showDentistInCard={settings.showDentistInCard}
                dentistSettings={settings.dentistSettings}
                showTimeInCard={settings.showTimeInCard}
                draggingId={draggingId}
                deadlineOrders={col.sala ? undefined : deadlineOrdersByDay.get(dayKey)}
                linhasDaFaixa={linhasDaFaixa}
                onDeadlineOrderClick={onOrderClick}
                onAppointmentEdit={onAppointmentEdit}
                onAppointmentStatusChange={onAppointmentStatusChange}
                onAppointmentIniciarAtendimento={onAppointmentIniciarAtendimento}
                onAppointmentEncerrarAtendimento={onAppointmentEncerrarAtendimento}
                atendimentoPendente={atendimentoPendente}
                onAppointmentDelete={onAppointmentDelete}
                onSlotDoubleClick={(d, t) => onSlotDoubleClick?.(d, t, col.sala ? (col.sala.chave === SEM_SALA ? null : col.sala.chave) : undefined)}
                onAppointmentResize={(id, newStart, newEnd) => {
                  onAppointmentMove?.(id, date, newStart, newEnd);
                }}
                onHover={(h) => setHoveredRange(h && { ...h, sala: col.sala?.chave })}
                highlightRange={rangeForColumn}
                compact={colunas.length > 8}
              />
            );
          })}
        </div>
      </div>

      {/* DragOverlay — card clone que segue o cursor via GPU transform.
          Zero re-render do grid durante arraste. */}
      <DragOverlay dropAnimation={null}>
        {draggingAppt && draggingColor ? (
          <DragFloatingCard
            patientName={draggingAppt.patientName}
            color={draggingColor}
            targetStart={hoveredRange?.start ?? draggingAppt.startTime}
            targetEnd={hoveredRange?.end ?? draggingAppt.endTime}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
